import {
  batchCreateBasicField,
  batchCreateNameSuper,
  batchCreatePerson,
  batchCreatePersonWithSharedSurname,
  batchCreateRelationship,
  batchDeleteElementAndSubs,
  batchMovePersonToRelationshipRecord,
  createFieldValue,
  createHighlightsDispatches,
  getNestedOrFloatingElementById,
  getNextUuid,
  INDEXED_INCORRECTLY,
  sexTypes,
} from '@fs/zion-sls'
import { searchForShareableHighlight } from '../../../utils/imageHighlightUtils'
import {
  gatherSurnameInformation,
  handleDeleteField,
} from '../../indexing/components/editPanels/utils/editPanelFieldHelpers'
import { superDeduper } from '../../indexing/components/editPanels/utils/editPanelDispatchBuilders'
import { FamilyReviewApp } from '../../../../shared/services/config'
import { getRelTypeAndOrder, handleUpdateSharedField } from './utils'

import saveSls from '../../../utils/taskSaveUtils'
import { stepsMap } from '../providers/FamilyReviewReducer'
import { checkIfHighlightIsShareable } from './highlightUtils'
import {
  searchForShareableSurnameAndFixHighShare,
  editNameSurn,
  editShareableSurnameExtension,
  createName,
} from './savePersonFunctions.utils'

const editPerson = async ({
  currentTask,
  partitionKey,
  formValues,
  dirtyFields,
  unlinkingNames,
  personId,
  userId,
  nameElementId,
  highlights,
  relatedPersonId,
  zionSLSState,
  zionSlsDispatch,
  saveChangesFlag,
  handleError,
  handleSuccess,
}) => {
  const selectedPerson = getNestedOrFloatingElementById(zionSLSState.images, personId)
  const { surnameElId, surnamePartitionKey, hasSecondarySurnameEl } = gatherSurnameInformation(
    formValues,
    selectedPerson
  )
  const attr = { comment: '', reasonId: '', app: FamilyReviewApp } // Eric said to use an empty reason id when creating new fields
  let dispatches = []
  let updatedHighlights = highlights

  let existingOrNewNameId = nameElementId
  if (!existingOrNewNameId) {
    existingOrNewNameId = getNextUuid()
    dispatches = dispatches.concat(
      batchCreateNameSuper({
        partitionKey: selectedPerson.partitionKey,
        nameElementId: existingOrNewNameId,
        nameType: 'BIRTH_NAME',
        personId: selectedPerson.id,
      })
    )
  }

  const createDispatchesToChangeExistingField = (existingField, key, value) => {
    const fieldWasRemoved = value === 'REMOVED' || value?.key === 'OTHER'
    const fieldValueWasChanged = dirtyFields[key]
    const fieldSharedByHighlight = checkIfHighlightIsShareable(updatedHighlights, key, currentTask)
    const fieldWasUnlinked = unlinkingNames[existingField.fieldType]
    const isSexField = sexTypes.includes(value?.key)
    if (fieldWasRemoved) {
      // Remove the field
      dispatches = dispatches.concat(
        handleDeleteField(existingField, key, hasSecondarySurnameEl, selectedPerson, zionSLSState.images)
      )
    } else if (fieldSharedByHighlight.sharedField) {
      updatedHighlights = fieldSharedByHighlight.updatedHighlights
      dispatches = dispatches.concat(
        handleUpdateSharedField(currentTask, selectedPerson, key, value, fieldSharedByHighlight.sharedField)
      )
    } else if (fieldValueWasChanged || fieldWasUnlinked) {
      if (existingField.elementType === 'RELATIONSHIP') {
        // To change relationship type, remove old relationship and add new relationship
        dispatches = dispatches.concat(batchDeleteElementAndSubs({ element: existingField }))
        const { relType, order } = getRelTypeAndOrder(value)
        dispatches = dispatches.concat(
          batchCreateRelationship({
            partitionKey,
            person1ID: order === 'FIRST' ? personId : relatedPersonId,
            person2ID: order === 'FIRST' ? relatedPersonId : personId,
            person1PartitionKey: partitionKey,
            person2PartitionKey: partitionKey,
            relType,
            id: getNextUuid(),
          })
        )
      } else if (existingField.fieldType === 'NAME_SURN') {
        const result = editNameSurn(
          updatedHighlights,
          existingField,
          value,
          { ...attr, reasonId: INDEXED_INCORRECTLY },
          unlinkingNames,
          existingOrNewNameId,
          zionSLSState,
          currentTask
        )
        dispatches = dispatches.concat(result.dispatches)
        updatedHighlights = result.updatedHighlights
      } else if (['NAME_PATR', 'NAME_MATR', 'NAME_SPRE'].includes(existingField.fieldType)) {
        const result = editShareableSurnameExtension(
          updatedHighlights,
          existingField,
          value,
          { ...attr, reasonId: INDEXED_INCORRECTLY },
          unlinkingNames,
          personId,
          zionSLSState
        )
        dispatches = dispatches.concat(result.dispatches)
        updatedHighlights = result.updatedHighlights
      } else if (isSexField) {
        const fieldValue = createFieldValue(value?.primaryText, attr, `http://gedcomx.org/${value?.key}`)
        dispatches.push({
          type: 'ADD_FV',
          value: { partitionKey: existingField.partitionKey, id: existingField.id, fieldValue },
        })
      } else {
        // Editing unsharable field
        const fieldValue = createFieldValue(value, { ...attr, reasonId: INDEXED_INCORRECTLY })
        dispatches.push({
          type: 'ADD_FV',
          value: { partitionKey: existingField.partitionKey, id: existingField.id, fieldValue },
        })
      }
    } else {
      const result = searchForShareableHighlight(updatedHighlights, key, value, zionSLSState.images)
      if (result.sharedField) {
        updatedHighlights = result.updatedHighlights
        existingField.superElements.forEach((superEl) => {
          const superElement = getNestedOrFloatingElementById(zionSLSState.images, superEl.id)
          dispatches.push(
            {
              type: 'REM_SUB',
              value: {
                superPartitionKey: superElement.partitionKey,
                subPartitionKey: existingField.partitionKey,
                superId: superElement.id,
                subId: existingField.id,
                elementType: superElement.elementType,
              },
            },
            {
              type: 'ADD_SUB',
              value: {
                superPartitionKey: superElement.partitionKey,
                subPartitionKey: result.sharedField.partitionKey,
                superId: superElement.id,
                subId: result.sharedField.id,
                elementType: superElement.elementType,
              },
            }
          )
        })
      }
    }
  }
  Object.entries(formValues)
    // Some entries are something like 50a89ad5-76f5-4a74-a614-76bea7f74887_fieldType: "REL_TYPE" and indicate a field's type. We don't need dispatches for these.
    .filter(([key, value]) => {
      if (key.includes('_fieldType')) return false
      if (typeof value === 'string') return value?.length >= 0
      return true
    })
    .forEach(([key, value]) => {
      const existingField = getNestedOrFloatingElementById([selectedPerson], key)
      const creatingNewRelationshipField = formValues[`${key}_fieldType`] === 'REL_TYPE'
      const creatingNewSexField = formValues[`${key}_fieldType`] === 'SEX_CODE'
      if (existingField) {
        if (!value) {
          dispatches = dispatches.concat(batchDeleteElementAndSubs({ element: existingField }))
        } else {
          createDispatchesToChangeExistingField(existingField, key, value)
        }
      } else if (creatingNewRelationshipField) {
        if (value && value?.key !== 'OTHER') {
          // Create new relationship
          const { relType, order } = getRelTypeAndOrder(value)

          dispatches = dispatches.concat(
            batchCreateRelationship({
              partitionKey,
              person1ID: order === 'FIRST' ? personId : relatedPersonId,
              person2ID: order === 'FIRST' ? relatedPersonId : personId,
              person1PartitionKey: partitionKey,
              person2PartitionKey: partitionKey,
              relType,
              id: getNextUuid(),
            })
          )
        }
      } else if (creatingNewSexField) {
        const result = searchForShareableHighlight(updatedHighlights, key, value, zionSLSState.images)
        if (result.sharedField) {
          // Add shared field as a sub
          updatedHighlights = result.updatedHighlights
          dispatches.push({
            type: 'ADD_SUB',
            value: {
              superPartitionKey: selectedPerson.partitionKey,
              subPartitionKey: result[0].partitionKey,
              superId: personId,
              subId: result.sharedField.id,
              elementType: 'PERSON',
            },
          })
        }

        // Create new SEX_CODE field
        const fieldValue = createFieldValue(value.primaryText, attr, `http://gedcomx.org/${value.key}`)
        // create any other type of field
        const batchCreateAdditionalFieldParams = {
          partitionKey: selectedPerson.partitionKey,
          superId: personId,
          id: key,
          fieldType: formValues[`${key}_fieldType`],
          fieldValue,
        }
        dispatches = dispatches.concat(batchCreateBasicField(batchCreateAdditionalFieldParams))
      } else {
        const result = createName({
          highlights,
          key,
          value,
          attr,
          formValues,
          selectedPerson,
          partitionKey,
          surnamePartitionKey,
          surnameElId,
          nameElementId: existingOrNewNameId,
          zionSLSState,
        })
        dispatches = dispatches.concat(result.dispatches)
        updatedHighlights = result.updatedHighlights
      }
    })

  dispatches = superDeduper(
    dispatches.concat(createHighlightsDispatches(zionSLSState.images, partitionKey, updatedHighlights, null, true))
  )

  dispatches.push({
    type: 'SET_RECORD_PERSON_IDS',
    value: selectedPerson.recordId,
  })

  await saveSls({
    dispatches,
    userId,
    taskTypeParam: 'Family Review',
    appName: FamilyReviewApp,
    saveChangesFlag,
    zionSLSState,
    zionSlsDispatch,
    handleError,
    handleSuccess,
  })
}

async function saveNewPerson({
  partitionKey,
  addNewPersonData,
  personId,
  userId,
  recordId,
  highlights,
  setAsPrimary,
  relatedPersonId,
  zionSLSState,
  zionSlsDispatch,
  saveChangesFlag,
  currentTask,
}) {
  let dispatches = []
  const attr = { comment: '', reasonId: '', app: FamilyReviewApp } // Eric said to use an empty reason id when creating new fields
  const result = searchForShareableSurnameAndFixHighShare(highlights, addNewPersonData.surnameFieldId, currentTask)
  dispatches = dispatches.concat(result.dispatches)
  if (result.sharedField) {
    dispatches = dispatches.concat(
      batchCreatePersonWithSharedSurname({
        partitionKey,
        personId,
        recordId,
        setAsPrimary,
        givenNameFieldId: addNewPersonData.givenNameFieldId,
        surnameFieldId: result.sharedField.id,
        givenNameFieldValue: addNewPersonData.givenField ? createFieldValue(addNewPersonData.givenField, attr) : null,
        nameType: addNewPersonData.nameType,
      })
    )
    dispatches.push({
      type: 'ADD_FV',
      value: {
        partitionKey,
        id: result.sharedField.id,
        fieldValue: createFieldValue(addNewPersonData.surnameField, attr),
      },
    })
  } else {
    dispatches = batchCreatePerson({
      partitionKey,
      personId,
      recordId,
      primaryPerson: setAsPrimary,
      givenNameFieldId: addNewPersonData.givenNameFieldId,
      surnameFieldId: addNewPersonData.surnameFieldId,
      givenNameFieldValue: addNewPersonData.givenField ? createFieldValue(addNewPersonData.givenField, attr) : null,
      surnameFieldValue: addNewPersonData.surnameField ? createFieldValue(addNewPersonData.surnameField, attr) : null,
      nameType: addNewPersonData.nameType,
    })
  }

  if (setAsPrimary) {
    const currentPrimaries = currentTask.subElements.filter((p) => p.elementType === 'PERSON' && p.primary)
    currentPrimaries.forEach((primary) => {
      dispatches.push({
        type: 'SET_PRIMARY',
        value: {
          partitionKey,
          id: primary.id,
          primary: false,
        },
      })
    })
  }

  const { sharedField, updatedHighlights } = searchForShareableHighlight(
    result.updatedHighlights,
    addNewPersonData.sexFieldId,
    addNewPersonData.sexField,
    zionSLSState.images
  )
  if (sharedField) {
    dispatches.push({
      type: 'ADD_SUB',
      value: {
        superPartitionKey: partitionKey,
        subPartitionKey: partitionKey,
        superId: personId,
        subId: sharedField.id,
        elementType: 'PERSON',
      },
    })
  } else {
    dispatches = dispatches.concat(
      batchCreateBasicField({
        partitionKey,
        superId: personId,
        fieldType: 'SEX_CODE',
        fieldValue: createFieldValue(
          addNewPersonData.sexField.primaryText,
          attr,
          `http://gedcomx.org/${addNewPersonData.sexField.key}`
        ),
        id: addNewPersonData.sexFieldId,
      })
    )
  }

  if (addNewPersonData.relationshipField) {
    const { relType, order } = getRelTypeAndOrder(addNewPersonData.relationshipField)

    dispatches = dispatches.concat(
      batchCreateRelationship({
        partitionKey,
        person1ID: order === 'FIRST' ? personId : relatedPersonId,
        person2ID: order === 'FIRST' ? relatedPersonId : personId,
        person1PartitionKey: partitionKey,
        person2PartitionKey: partitionKey,
        relType,
        id: getNextUuid(),
      })
    )
  }
  dispatches = dispatches.concat(
    createHighlightsDispatches(zionSLSState.images, partitionKey, updatedHighlights, null, true)
  )

  dispatches.push({
    type: 'SET_RECORD_PERSON_IDS',
    value: recordId,
  })

  await saveSls({
    dispatches,
    userId,
    taskTypeParam: 'Family Review',
    appName: FamilyReviewApp,
    saveChangesFlag,
    zionSLSState,
    zionSlsDispatch,
  })
}

async function saveExistingPerson({
  partitionKey,
  selectedPerson,
  primaryPerson,
  currentStep,
  images,
  userId,
  zionSLSState,
  zionSlsDispatch,
  saveChangesFlag,
}) {
  const dispatches = batchMovePersonToRelationshipRecord({
    mainPerson: primaryPerson,
    otherPerson: selectedPerson,
    images,
  })
  if (currentStep === stepsMap.STEP_ONE) {
    dispatches.push({
      type: 'SET_PRIMARY',
      value: {
        partitionKey,
        id: selectedPerson.id,
        primary: true,
      },
    })
    dispatches.push({
      type: 'SET_PRIMARY',
      value: {
        partitionKey,
        id: primaryPerson.id,
        primary: false,
      },
    })
  }

  await saveSls({
    dispatches,
    userId,
    taskTypeParam: 'Family Review',
    appName: FamilyReviewApp,
    saveChangesFlag,
    zionSLSState,
    zionSlsDispatch,
  })
}

export { editPerson, saveExistingPerson, saveNewPerson, editNameSurn, editShareableSurnameExtension, createName }
